import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaFacebook, FaApple, FaEye, FaEyeSlash, FaSpinner, FaCheckCircle, FaShieldAlt, FaHeart } from 'react-icons/fa';
import { FunctionComponent } from 'react';
import { loginUser } from '../lib/api';
import axios from 'axios';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

interface Props {
  setIsLoggedIn: (value: boolean) => void;
}

const Login: React.FC<Props> = ({ setIsLoggedIn }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const FaFacebookIcon = FaFacebook as FunctionComponent<{ className?: string }>;
  const FaAppleIcon = FaApple as FunctionComponent<{ className?: string }>;
  const FaEyeIcon = FaEye as FunctionComponent<{ className?: string }>;
  const FaEyeSlashIcon = FaEyeSlash as FunctionComponent<{ className?: string }>;
  const FaSpinnerIcon = FaSpinner as FunctionComponent<{ className?: string }>;
  const FaCheckCircleIcon = FaCheckCircle as FunctionComponent<{ className?: string }>;
  const FaShieldAltIcon = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaHeartIcon = FaHeart as FunctionComponent<{ className?: string }>;

  useEffect(() => {
    const message = (location.state as any)?.message;
    if (message) {
      setSuccess(message);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { jwt, user } = await loginUser({ identifier, password });
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('userId', user.id.toString());
      setIsLoggedIn(true);
      const from = (location.state as any)?.from || '/';
      navigate(from);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await axios.get('https://www.eatmeatapp.com/api/auth/google/callback', {
        params: { access_token: credentialResponse.credential },
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res.status === 200) {
        const { jwt, user } = res.data;
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('userId', user.id.toString());
        setIsLoggedIn(true);
        const isNewUser = new Date(user.createdAt).getTime() > Date.now() - 1000 * 60;
        const from = isNewUser ? '/bmi' : ((location.state as any)?.from || '/');
        navigate(from);
      } else {
        setError(res.data?.error?.message || 'Google Sign-In failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Google Sign-In failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In was canceled or failed');
    setIsLoading(false);
  };

  const handleAppleSuccess = async (response: any) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const idToken = response.authorization.id_token;
      if (!idToken) throw new Error('Apple Sign-In failed: No ID token received');
      const res = await axios.get('https://www.eatmeatapp.com/api/auth/apple/callback', {
        params: { access_token: idToken },
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res.status === 200) {
        const { jwt, user } = res.data;
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('userId', user.id.toString());
        setIsLoggedIn(true);
        const isNewUser = new Date(user.createdAt).getTime() > Date.now() - 1000 * 60;
        const from = isNewUser ? '/bmi' : ((location.state as any)?.from || '/');
        navigate(from);
      } else {
        setError(res.data?.error?.message || 'Apple Sign-In failed');
      }
    } catch (err: any) {
      setError(err.message || 'Apple Sign-In failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleError = (error: any) => {
    setError(error.message || 'Apple Sign-In was canceled or failed');
    setIsLoading(false);
  };

  const handleSocialLogin = (provider: string) => {
    const from = (location.state as any)?.from || '/';
    localStorage.setItem('socialLoginRedirect', from);
    window.location.href = `https://www.eatmeatapp.com/api/connect/${provider}`;
  };

  return (
    <GoogleOAuthProvider clientId="98928347282-p7lkbck0mcffnhmi39eriatmksavbmm9.apps.googleusercontent.com">
      <Helmet>
        <title>Log In — EatMeat</title>
        <meta name="description" content="Log in to your EatMeat account to access personalized carnivore and ketovore diet features." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex">
        {/* ── LEFT panel (hidden on mobile) ── */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white border-r border-gray-100 text-gray-800">
          <div className="relative z-10 flex flex-col justify-center p-12 w-full">
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Welcome back
                </div>
                <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight mb-4 text-gray-900">
                  Your <span className="text-primary">carnivore journey</span>, picked up where you left off
                </h2>
                <p className="text-gray-500 text-lg max-w-md">Track macros, plan meals, and connect with a thriving community.</p>
              </div>

              <div className="space-y-3 max-w-md">
                {[
                  { icon: FaHeartIcon, text: '500+ premium carnivore & keto recipes', color: 'bg-red-50 text-red-600' },
                  { icon: FaCheckCircleIcon, text: 'Daily macro tracking with progress rings', color: 'bg-green-50 text-green-600' },
                  { icon: FaShieldAltIcon, text: 'Private, secure, and always in your control', color: 'bg-blue-50 text-blue-600' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500 pt-4">
                <div className="flex -space-x-2">
                  {['🥩', '🧈', '🥓'].map((e, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm border-2 border-white shadow-sm">{e}</div>
                  ))}
                </div>
                <span>Joined by 10,000+ carnivores worldwide</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT panel (form) ── */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <div className="mb-6">
              <Link to="/" className="text-sm text-gray-500 hover:text-primary transition-colors">← Back to Home</Link>
            </div>

            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-extrabold text-text mb-1">Welcome back 👋</h1>
                <p className="text-gray-500 text-sm">Sign in to continue your journey</p>
              </div>

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2" role="alert">
                  <FaCheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-700">{success}</span>
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4" role="alert">
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="identifier" className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Email or Username</label>
                  <input
                    type="text" id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                    required autoComplete="username"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Password</label>
                    <Link to="/reset-password" className="text-xs text-primary font-semibold hover:underline">Forgot?</Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                      required autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary rounded-lg transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <FaEyeSlashIcon className="h-4 w-4" /> : <FaEyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" id="remember" className="w-4 h-4 accent-primary rounded" />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>

                <button type="submit" disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary to-orange-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all flex items-center justify-center">
                  {isLoading ? (<><FaSpinnerIcon className="animate-spin mr-2 h-4 w-4" /> Signing in…</>) : 'Sign In'}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-gray-400 font-medium uppercase tracking-wider">or continue with</span></div>
              </div>

              {/* Social buttons */}
              <div className="space-y-2.5">
                <div className="google-btn-wrapper">
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} size="large" width="360" theme="outline" shape="pill" />
                </div>

                <AppleSignin
                  authOptions={{
                    clientId: 'com.carivore.lifestyle.eatmeat.auth',
                    scope: 'email name',
                    redirectURI: 'https://www.eatmeatapp.com/api/connect/apple/callback',
                    state: 'state', nonce: 'nonce', usePopup: true,
                  }}
                  uiType="light" className="apple-auth-btn" noDefaultStyle={false}
                  buttonExtraChildren="Continue with Apple"
                  onSuccess={handleAppleSuccess} onError={handleAppleError} skipScript={false}
                  render={(props: any) => (
                    <button {...props} disabled={isLoading}
                      className="w-full bg-black text-white font-semibold py-3 rounded-full hover:bg-gray-900 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                      <FaAppleIcon className="h-5 w-5" /> Continue with Apple
                    </button>
                  )}
                />

                <button type="button" onClick={() => handleSocialLogin('facebook')} disabled={isLoading}
                  className="w-full bg-[#1877F2] text-white font-semibold py-3 rounded-full hover:bg-[#1564d1] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  <FaFacebookIcon className="h-5 w-5" /> Continue with Facebook
                </button>
              </div>

              <p className="mt-6 text-sm text-center text-gray-500">
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary font-semibold hover:underline">Sign up free</Link>
              </p>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-primary">Terms</Link> and{' '}
              <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default Login;
